/**
 * Modal de configuration des alertes CFO (Fév 2026).
 * Permet à l'admin/finance de configurer par compte :
 *  - Activer/Désactiver
 *  - Seuil de variation (%) — négatif pour alerter sur baisse
 *  - Plancher minimum (XOF)
 *  - Canaux : email + WhatsApp
 *  - Destinataires : emails + téléphones
 *
 * Endpoints : GET/PUT /api/bank/alerts/config/{account_id}
 *             POST    /api/bank/alerts/check-now?dry_run=true
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Bell, Mail, MessageSquare, Save, Loader2, PlayCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import StaffRecipientsSelector from '../common/StaffRecipientsSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BankAlertsConfigModal({ open, onClose, accountId, accountLabel }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const load = async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bank/alerts/config/${accountId}`);
      setConfig(data);
    } catch (e) {
      toast({ title: 'Erreur chargement config', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && accountId) load(); }, [open, accountId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/bank/alerts/config/${accountId}`, {
        enabled: config.enabled,
        variation_threshold_pct: Number(config.variation_threshold_pct),
        min_balance_floor: config.min_balance_floor === null || config.min_balance_floor === '' ? null : Number(config.min_balance_floor),
        alert_channels: config.alert_channels,
        alert_recipients: config.alert_recipients,
        staff_user_ids: config.staff_user_ids || [],
      });
      setConfig(data);
      toast({ title: 'Configuration enregistrée', description: 'Les alertes sont désormais actives.' });
    } catch (e) {
      toast({ title: 'Erreur sauvegarde', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const testAlerts = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/bank/alerts/check-now?dry_run=true`);
      const forThis = (data.results || []).filter(r => r.account_id === accountId);
      if (forThis.length === 0) {
        toast({ title: 'Test réussi', description: 'Aucune alerte à envoyer aujourd\'hui — la variation est dans les seuils.' });
      } else {
        toast({
          title: `${forThis.length} alerte(s) détectée(s)`,
          description: forThis.map(a => `${a.alert?.alert_type}: ${a.alert?.actual_value}`).join(' · '),
        });
      }
    } catch (e) {
      toast({ title: 'Erreur test', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setTesting(false); }
  };

  const toggleChannel = (ch) => {
    const has = (config.alert_channels || []).includes(ch);
    const next = has ? config.alert_channels.filter(c => c !== ch) : [...(config.alert_channels || []), ch];
    setConfig({ ...config, alert_channels: next.length ? next : ['email'] });
  };

  const addEmail = () => {
    if (!newEmail.trim()) return;
    const emails = [...(config.alert_recipients?.emails || []), newEmail.trim()];
    setConfig({ ...config, alert_recipients: { ...config.alert_recipients, emails } });
    setNewEmail('');
  };
  const removeEmail = (i) => {
    const emails = (config.alert_recipients?.emails || []).filter((_, idx) => idx !== i);
    setConfig({ ...config, alert_recipients: { ...config.alert_recipients, emails } });
  };
  const addPhone = () => {
    if (!newPhone.trim()) return;
    const phones = [...(config.alert_recipients?.phones || []), newPhone.trim()];
    setConfig({ ...config, alert_recipients: { ...config.alert_recipients, phones } });
    setNewPhone('');
  };
  const removePhone = (i) => {
    const phones = (config.alert_recipients?.phones || []).filter((_, idx) => idx !== i);
    setConfig({ ...config, alert_recipients: { ...config.alert_recipients, phones } });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bank-alerts-modal">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <div>
              <h3 className="font-semibold">Alertes CFO</h3>
              <p className="text-xs opacity-90">{accountLabel || accountId}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="bank-alerts-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading || !config ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Chargement…
          </div>
        ) : (
          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Toggle enabled */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                className="h-4 w-4" data-testid="bank-alerts-enabled" />
              <span className="text-sm font-medium text-slate-800">Activer les alertes automatiques</span>
            </label>

            {/* Variation threshold */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Seuil de variation vs mois précédent (%)
              </label>
              <input type="number" step="0.1"
                value={config.variation_threshold_pct ?? ''}
                onChange={(e) => setConfig({ ...config, variation_threshold_pct: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                placeholder="-15 (baisse) ou +50 (mouvement anormal)"
                data-testid="bank-alerts-variation" />
              <p className="text-[10px] text-slate-500 mt-1">
                Valeur négative → alerte si variation ≤ seuil (baisse). Valeur positive → alerte si variation ≥ seuil (hausse anormale).
              </p>
            </div>

            {/* Min balance floor */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Plancher minimum du solde (XOF)
              </label>
              <input type="number"
                value={config.min_balance_floor ?? ''}
                onChange={(e) => setConfig({ ...config, min_balance_floor: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
                placeholder="Laisser vide pour désactiver"
                data-testid="bank-alerts-floor" />
              <p className="text-[10px] text-slate-500 mt-1">Une alerte est envoyée dès que le solde de clôture passe sous ce plancher.</p>
            </div>

            {/* Canaux */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">Canaux de notification</label>
              <div className="flex gap-3">
                <button
                  onClick={() => toggleChannel('email')}
                  className={`flex-1 inline-flex items-center justify-center gap-2 border rounded-lg px-3 py-2 text-sm ${(config.alert_channels || []).includes('email') ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-600'}`}
                  data-testid="bank-alerts-channel-email">
                  <Mail className="h-4 w-4" /> Email
                </button>
                <button
                  onClick={() => toggleChannel('whatsapp')}
                  className={`flex-1 inline-flex items-center justify-center gap-2 border rounded-lg px-3 py-2 text-sm ${(config.alert_channels || []).includes('whatsapp') ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200 text-slate-600'}`}
                  data-testid="bank-alerts-channel-whatsapp">
                  <MessageSquare className="h-4 w-4" /> WhatsApp
                </button>
              </div>
            </div>

            {/* Emails */}
            {(config.alert_channels || []).includes('email') && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Destinataires email</label>
                <div className="flex gap-2 mb-2">
                  <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addEmail()}
                    className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5"
                    placeholder="cfo@baobab.ci" data-testid="bank-alerts-email-input" />
                  <button onClick={addEmail} className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded text-sm inline-flex items-center gap-1"
                    data-testid="bank-alerts-email-add"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <div className="space-y-1">
                  {(config.alert_recipients?.emails || []).map((e, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1 text-xs">
                      <span className="text-slate-700 truncate">{e}</span>
                      <button onClick={() => removeEmail(i)} className="text-rose-500 hover:text-rose-700" data-testid={`bank-alerts-email-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phones */}
            {(config.alert_channels || []).includes('whatsapp') && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Numéros WhatsApp</label>
                <div className="flex gap-2 mb-2">
                  <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addPhone()}
                    className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5"
                    placeholder="+225 07 07 07 07 07" data-testid="bank-alerts-phone-input" />
                  <button onClick={addPhone} className="bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1.5 rounded text-sm inline-flex items-center gap-1"
                    data-testid="bank-alerts-phone-add"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <div className="space-y-1">
                  {(config.alert_recipients?.phones || []).map((p, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1 text-xs">
                      <span className="text-slate-700 truncate">{p}</span>
                      <button onClick={() => removePhone(i)} className="text-rose-500 hover:text-rose-700" data-testid={`bank-alerts-phone-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Staff recipients — récupère email + phone auto */}
            {((config.alert_channels || []).includes('email') || (config.alert_channels || []).includes('whatsapp')) && (
              <div className="border-t border-slate-100 pt-3">
                <StaffRecipientsSelector
                  value={config.staff_user_ids || []}
                  onChange={(ids) => setConfig({ ...config, staff_user_ids: ids })}
                  testIdPrefix="bank-alerts-staff"
                />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 justify-end p-4 border-t border-slate-200 bg-slate-50">
          <button onClick={testAlerts} disabled={testing || !config?.enabled}
            className="inline-flex items-center gap-1 text-sm text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white disabled:opacity-50"
            data-testid="bank-alerts-test-btn">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Tester
          </button>
          <button onClick={onClose}
            className="text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white">
            Annuler
          </button>
          <button onClick={save} disabled={saving || !config}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-1.5 rounded-lg inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="bank-alerts-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
